/**
 * OpenCloseおよびOpenCloseManagerクラス
 *
 * 概要:
 * このファイルは、HTMLの開閉機能を提供する`OpenClose`と、それを複数管理する`OpenCloseManager`の2つのクラスを実装しています。
 * 開閉ボタンとコンテンツをリンクさせ、クリックイベントに基づいてコンテンツをスライド表示または非表示にします。
 *
 * 主な機能:
 * 1. **OpenCloseクラス**:
 *    - 特定の開閉ボタンとその対応するコンテンツを管理するクラス。
 *    - 開閉時のアニメーション、ARIA属性の管理、ボタンテキストの切り替えを行う。
 *
 * 2. **OpenCloseManagerクラス**:
 *    - ページ内のすべての開閉ボタンを自動的に検出し、`OpenClose`インスタンスを一括管理する。
 *    - 開閉ボタンやコンテンツの状態を一元管理し、必要に応じて全てを操作・破棄可能。
 *
 * 利用方法:
 * - 対応するHTML構造:
 *   ```
 *   <a class="ist-js__openCloseButton" href="#content1">開閉ボタン</a>
 *   <div id="content1" class="ist-js__openCloseContent">
 *     コンテンツ
 *   </div>
 *   ```
 *   - ボタンには`.ist-js__openCloseButton`クラスを付与し、`href`属性でコンテンツのIDを指定。
 *   - コンテンツ要素には特に追加クラスは不要。
 * - JavaScriptで初期化:
 *   ```
 *   const manager = new OpenCloseManager();
 *   ```
 *
 * 主要なカスタマイズポイント:
 * - アニメーション速度やイージング: `OpenClose`クラス内の`animationDuration`や`easing`で調整可能。
 * - ボタンのテキスト切り替え: `.ist-js__openCloseButtonText--open` と `.ist-js__openCloseButtonText--close` クラスを利用。
 *
 * クラスの責務:
 * - `OpenClose` は1つの開閉ボタンと対応するコンテンツの状態を管理。
 * - `OpenCloseManager` はページ内に存在する複数の開閉ボタンを管理。
 *
 * 注意事項:
 * - 必須のHTML構造（ボタンのhref属性とコンテンツのID）が正しく設定されていない場合、エラーをスローします。
 * - すべての初期化はページロード後に自動的に行われますが、必要に応じてマネージャーを破棄できます。
 *
 * 拡張性:
 * - 本コードをベースに、クリックアウトサイドで自動的に閉じる機能や複雑なアニメーションを追加することも可能です。
 *
 * 追加仕様01:
 * - 開いたときに、hrefで指定しているIDの要素にスクロールして表示する
 *
 * 追加仕様02:
 * - 開いたときに、`data-scroll-to`属性が指定されていて、かつその要素が存在する場合、その要素にスクロールして表示する
 */

/**
 * OpenCloseクラスは、開閉ボタンと関連するコンテンツの表示・非表示を管理する。
 */
export class OpenClose {
  private button: HTMLElement; // 開閉ボタンの要素
  private content: HTMLElement; // 対応するコンテンツの要素
  private openText: HTMLElement | null; // ボタンの開く時のテキスト
  private closeText: HTMLElement | null; // ボタンの閉じる時のテキスト
  private animationDuration: number = 300; // アニメーションの長さ（ミリ秒）
  private easing: string = 'ease-out'; // アニメーションのイージング設定

  /**
   * コンストラクタ
   * @param button - 開閉ボタン要素
   */
  constructor(button: HTMLElement) {
    this.button = button;

    // ボタンのhref属性から対応するコンテンツのIDを取得
    const href = this.button.getAttribute('href');
    if (!href || !href.startsWith('#')) {
      throw new Error(
        'ボタンには、コンテンツIDを指定する有効なhref属性が必要です。'
      );
    }

    // IDを使用してコンテンツ要素を取得
    const content = document.querySelector(href);
    if (!(content instanceof HTMLElement)) {
      throw new Error('コンテンツ要素が見つかりません。');
    }
    this.content = content;

    // ボタン内のテキスト要素を取得
    this.openText = this.button.querySelector(
      '.ist-js__openCloseButtonText--open'
    );
    this.closeText = this.button.querySelector(
      '.ist-js__openCloseButtonText--close'
    );

    this.init();
  }

  /**
   * 初期化処理を実行
   */
  private init(): void {
    // コンテンツの初期状態を設定
    this.content.style.height = '0px';
    this.content.style.transition = `height ${this.animationDuration}ms ${this.easing}`;
    this.content.style.overflow = 'hidden';
    this.content.style.display = 'none'; // 初期状態で非表示に設定
    this.content.setAttribute('aria-hidden', 'true');
    this.content.setAttribute('role', 'region');

    // ボタンのテキストの初期状態を設定
    if (this.openText) this.openText.style.display = '';
    if (this.closeText) this.closeText.style.display = 'none';

    // ボタンの初期属性を設定
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', this.content.id);

    // ボタンクリックイベントを設定
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggleContent(); // 開閉トグル
    });
  }

  /**
   * コンテンツの開閉を切り替える
   */
  private toggleContent(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * 現在コンテンツが開いているか確認する
   * @returns 開いている場合はtrue、閉じている場合はfalse
   */
  private isOpen(): boolean {
    return this.content.style.display !== 'none';
  }

  /**
   * コンテンツを開く
   */
  private open(): void {
    this.content.style.display = ''; // 表示を解除
    const targetHeight = `${this.content.scrollHeight}px`;
    this.content.style.height = targetHeight;
    this.content.style.overflow = 'hidden'; // アニメーション中はoverflowをhiddenに設定
    this.content.setAttribute('aria-hidden', 'false');
    this.button.setAttribute('aria-expanded', 'true');
    this.button.classList.add('ist-js__openCloseButton--open');

    // ボタンテキストを切り替え
    if (this.openText) this.openText.style.display = 'none';
    if (this.closeText) this.closeText.style.display = '';

    // アニメーション完了後の後処理
    setTimeout(() => {
      this.content.style.height = 'auto';
      this.content.style.overflow = ''; // アニメーション終了後にoverflowを解除

      // 追加仕様02:
      // data-scroll-to属性が指定され、かつその要素が存在する場合は、そちらにスクロール
      const scrollToId = this.button.getAttribute('data-scroll-to');
      if (scrollToId) {
        const targetElement = document.getElementById(scrollToId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return; // 指定要素が存在した場合は、これ以上の処理は不要
        }
      }
      // data-scroll-to属性がない、または指定要素が存在しない場合は、href属性で指定したコンテンツへスクロール
      this.content.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, this.animationDuration);
  }

  /**
   * コンテンツを閉じる
   */
  private close(): void {
    const startHeight = `${this.content.scrollHeight}px`;
    this.content.style.height = startHeight;
    this.content.style.overflow = 'hidden'; // アニメーション中はoverflowをhiddenに設定

    // 次フレームで高さを0に設定（アニメーション開始）
    requestAnimationFrame(() => {
      this.content.style.height = '0px';
    });

    // 属性とクラスを更新
    this.content.setAttribute('aria-hidden', 'true');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.classList.remove('ist-js__openCloseButton--open');

    // ボタンテキストを切り替え
    if (this.openText) this.openText.style.display = '';
    if (this.closeText) this.closeText.style.display = 'none';

    // アニメーション完了後の後処理
    setTimeout(() => {
      this.content.style.display = 'none'; // アニメーション終了後に非表示に設定
    }, this.animationDuration);
  }
}

/**
 * OpenCloseManagerクラスは、複数のOpenCloseインスタンスを一括管理する。
 */
export class OpenCloseManager {
  private openCloses: OpenClose[] = []; // 管理対象のOpenCloseインスタンスのリスト

  /**
   * コンストラクタ
   */
  constructor() {
    this.init();
  }

  /**
   * 初期化処理
   */
  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeOpenCloses();
      });
    } else {
      this.initializeOpenCloses();
    }
  }

  /**
   * 各ボタンを対象としたOpenCloseインスタンスを生成
   */
  private initializeOpenCloses(): void {
    const buttons = document.querySelectorAll('.ist-js__openCloseButton');
    buttons.forEach((button) => {
      if (button instanceof HTMLElement) {
        this.openCloses.push(new OpenClose(button));
      }
    });
  }

  /**
   * 管理中の全OpenCloseインスタンスを取得
   */
  public getOpenCloses(): OpenClose[] {
    return this.openCloses;
  }

  /**
   * 全てのOpenCloseインスタンスの管理を解除
   */
  public destroy(): void {
    this.openCloses = [];
  }
}
